import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  Space,
  Divider,
  Progress,
  Alert,
  Empty,
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  TrophyOutlined,
  WarningOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { reportService } from '../../services';
import { formatNumber, formatDate, formatCurrency } from '../../utils/helpers';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const StockReports = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [reportType, setReportType] = useState('overview');
  
  // Data states
  const [bestSellingProducts, setBestSellingProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [revenueData, setRevenueData] = useState({});
  const [orderStatusSummary, setOrderStatusSummary] = useState([]);

  useEffect(() => {
    loadReports();
  }, [dateRange, reportType]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
      };

      // Load all reports in parallel
      const promises = [];
      
      if (reportType === 'overview' || reportType === 'bestselling') {
        promises.push(
          reportService.getBestSellingProducts(params).then(res => {
            if (res.success) setBestSellingProducts(res.data || []);
          })
        );
      }

      if (reportType === 'overview' || reportType === 'lowstock') {
        promises.push(
          reportService.getLowStockProducts(params).then(res => {
            if (res.success) setLowStockProducts(res.data || []);
          })
        );
      }

      if (reportType === 'overview' || reportType === 'revenue') {
        promises.push(
          reportService.getTotalRevenue(params).then(res => {
            if (res.success) setRevenueData(res.data || {});
          })
        );
      }

      if (reportType === 'overview' || reportType === 'orders') {
        promises.push(
          reportService.getOrderStatusSummary(params).then(res => {
            if (res.success) setOrderStatusSummary(res.data || []);
          })
        );
      }

      await Promise.all(promises);
    } catch (error) {
      //console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    //console.log('Export reports');
  };

  const bestSellingColumns = [
    {
      title: 'Hạng',
      key: 'rank',
      width: 60,
      render: (_, __, index) => (
        <div style={{ textAlign: 'center' }}>
          {index < 3 ? (
            <TrophyOutlined style={{ 
              color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
              fontSize: '16px'
            }} />
          ) : (
            <span style={{ fontWeight: 'bold' }}>{index + 1}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            SKU: {record.sku || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Đã bán',
      dataIndex: 'total_sold',
      key: 'total_sold',
      render: (sold) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {formatNumber(sold)}
          </div>
        </div>
      ),
      sorter: (a, b) => (a.total_sold || 0) - (b.total_sold || 0),
    },
    {
      title: 'Doanh thu',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (revenue) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
            {formatCurrency(revenue)}
          </div>
        </div>
      ),
      sorter: (a, b) => (a.total_revenue || 0) - (b.total_revenue || 0),
    },
    {
      title: 'Tồn kho',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (stock, record) => {
        const stockStatus = stock <= (record.min_stock || 0) ? 'low' : 'normal';
        return (
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              fontSize: '14px',
              color: stockStatus === 'low' ? '#fa8c16' : '#666'
            }}>
              {formatNumber(stock)}
              {stockStatus === 'low' && <WarningOutlined style={{ marginLeft: 4, color: '#fa8c16' }} />}
            </div>
          </div>
        );
      },
    },
  ];

  const lowStockColumns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            SKU: {record.sku || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Tồn kho hiện tại',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (stock) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fa8c16' }}>
            {formatNumber(stock)}
          </div>
        </div>
      ),
    },
    {
      title: 'Mức tối thiểu',
      dataIndex: 'min_stock',
      key: 'min_stock',
      render: (minStock) => formatNumber(minStock || 0),
    },
    {
      title: 'Cần nhập',
      key: 'need_stock',
      render: (_, record) => {
        const needed = Math.max(0, (record.max_stock || 100) - (record.current_stock || 0));
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
              {formatNumber(needed)}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Tình trạng',
      key: 'status',
      render: (_, record) => {
        const stock = record.current_stock || 0;
        const minStock = record.min_stock || 0;
        
        if (stock === 0) {
          return <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Hết hàng</div>;
        } else if (stock <= minStock) {
          return <div style={{ color: '#fa8c16', fontWeight: 'bold' }}>Sắp hết</div>;
        }
        return <div style={{ color: '#52c41a' }}>Bình thường</div>;
      },
    },
  ];

  const orderStatusColumns = [
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          'pending': 'Chờ xử lý',
          'processing': 'Đang xử lý',
          'shipping': 'Đang giao',
          'delivered': 'Đã giao',
          'completed': 'Hoàn thành',
          'cancelled': 'Đã hủy',
        };
        return statusMap[status] || status;
      },
    },
    {
      title: 'Số lượng đơn',
      dataIndex: 'order_count',
      key: 'order_count',
      render: (count) => formatNumber(count),
    },
    {
      title: 'Tỷ lệ',
      key: 'percentage',
      render: (_, record) => {
        const total = orderStatusSummary.reduce((sum, item) => sum + (item.order_count || 0), 0);
        const percentage = total > 0 ? ((record.order_count || 0) / total * 100) : 0;
        return (
          <div style={{ width: 120 }}>
            <Progress 
              percent={percentage} 
              size="small" 
              format={percent => `${percent.toFixed(1)}%`}
            />
          </div>
        );
      },
    },
    {
      title: 'Doanh thu',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (revenue) => formatCurrency(revenue || 0),
    },
  ];

  const getRevenueGrowth = () => {
    if (revenueData.current_period && revenueData.previous_period) {
      const current = revenueData.current_period;
      const previous = revenueData.previous_period;
      if (previous > 0) {
        return ((current - previous) / previous * 100).toFixed(1);
      }
    }
    return '0.0';
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={3}>Báo cáo kho</Title>
        </div>

        {/* Bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              style={{ width: '100%' }}
              ranges={{
                'Hôm nay': [dayjs(), dayjs()],
                '7 ngày qua': [dayjs().subtract(7, 'days'), dayjs()],
                '30 ngày qua': [dayjs().subtract(30, 'days'), dayjs()],
                'Tháng này': [dayjs().startOf('month'), dayjs()],
                'Tháng trước': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
              }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              value={reportType}
              onChange={setReportType}
              style={{ width: '100%' }}
            >
              <Option value="overview">Tổng quan</Option>
              <Option value="bestselling">Sản phẩm bán chạy</Option>
              <Option value="lowstock">Tồn kho thấp</Option>
              <Option value="revenue">Doanh thu</Option>
              <Option value="orders">Đơn hàng</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadReports}
                loading={loading}
              >
                Làm mới
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                Xuất báo cáo
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Tổng quan - Thống kê nhanh */}
        {(reportType === 'overview' || reportType === 'revenue') && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Doanh thu"
                  value={revenueData.current_period || 0}
                  formatter={(value) => formatCurrency(value)}
                  prefix={<DollarOutlined />}
                  suffix={
                    <span style={{ 
                      fontSize: '12px',
                      color: parseFloat(getRevenueGrowth()) >= 0 ? '#52c41a' : '#ff4d4f'
                    }}>
                      {parseFloat(getRevenueGrowth()) >= 0 ? '+' : ''}{getRevenueGrowth()}%
                    </span>
                  }
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Tổng đơn hàng"
                  value={orderStatusSummary.reduce((sum, item) => sum + (item.order_count || 0), 0)}
                  formatter={(value) => formatNumber(value)}
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Sản phẩm bán chạy"
                  value={bestSellingProducts.length}
                  prefix={<TrophyOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Sản phẩm sắp hết"
                  value={lowStockProducts.length}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: lowStockProducts.length > 0 ? '#fa8c16' : '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Cảnh báo tồn kho thấp */}
        {lowStockProducts.length > 0 && (reportType === 'overview' || reportType === 'lowstock') && (
          <Alert
            message={`Cảnh báo: Có ${lowStockProducts.length} sản phẩm đang trong tình trạng tồn kho thấp hoặc hết hàng!`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={() => setReportType('lowstock')}>
                Xem chi tiết
              </Button>
            }
          />
        )}

        {/* Báo cáo sản phẩm bán chạy */}
        {(reportType === 'overview' || reportType === 'bestselling') && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BarChartOutlined style={{ marginRight: 8 }} />
                Sản phẩm bán chạy nhất
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {bestSellingProducts.length > 0 ? (
              <Table
                columns={bestSellingColumns}
                dataSource={bestSellingProducts}
                rowKey="product_id"
                pagination={{ pageSize: 10 }}
                size="small"
              />
            ) : (
              <Empty description="Không có dữ liệu sản phẩm bán chạy" />
            )}
          </Card>
        )}

        {/* Báo cáo tồn kho thấp */}
        {(reportType === 'overview' || reportType === 'lowstock') && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <WarningOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                Sản phẩm tồn kho thấp
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {lowStockProducts.length > 0 ? (
              <Table
                columns={lowStockColumns}
                dataSource={lowStockProducts}
                rowKey="product_id"
                pagination={{ pageSize: 10 }}
                size="small"
              />
            ) : (
              <Empty description="Không có sản phẩm nào có tồn kho thấp" />
            )}
          </Card>
        )}

        {/* Báo cáo trạng thái đơn hàng */}
        {(reportType === 'overview' || reportType === 'orders') && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <PieChartOutlined style={{ marginRight: 8 }} />
                Tổng hợp trạng thái đơn hàng
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {orderStatusSummary.length > 0 ? (
              <Table
                columns={orderStatusColumns}
                dataSource={orderStatusSummary}
                rowKey="status"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="Không có dữ liệu đơn hàng" />
            )}
          </Card>
        )}

        {/* Báo cáo doanh thu chi tiết */}
        {(reportType === 'revenue') && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LineChartOutlined style={{ marginRight: 8 }} />
                Chi tiết doanh thu
              </div>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Kỳ hiện tại"
                    value={revenueData.current_period || 0}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Kỳ trước"
                    value={revenueData.previous_period || 0}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Tăng trưởng"
                    value={getRevenueGrowth()}
                    suffix="%"
                    valueStyle={{ 
                      color: parseFloat(getRevenueGrowth()) >= 0 ? '#3f8600' : '#cf1322'
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default StockReports;
